import { NavigateBefore, NavigateNext } from '@mui/icons-material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { alpha, Box, Drawer, IconButton, Stack, Typography, type Theme } from '@mui/material';
import useBreakpoints from '../../../../hooks/useBreakpoints';
import useShouldShowBetslip from '../../../../hooks/useShouldShowBetslip';
import { useReofferedBetslip } from '../../../../hooks/useReofferedBetslip';
import { NEOBET_HEADER_HEIGHT } from '../../../../config/skinRegistry/configs';
import ReofferedBetslipCountdown from '../../../atoms/ReofferedBetslipCountdown';
import ReofferedBetslipActions from '../../../molecules/ReofferedBetslipActions';
import NeobetReofferedSelectionCard from '../molecules/NeobetReofferedSelectionCard';
import NeobetReofferedSummary from '../molecules/NeobetReofferedSummary';
import NeobetReofferedBetBuilderItem from '../molecules/NeobetReofferedBetBuilderItem';

import { mergeSx } from '../../../../utils/styles';

const DESKTOP_PANEL_WIDTH = 432;

const styles = {
  desktopRail: {
    position: 'relative',
    flex: `0 0 ${DESKTOP_PANEL_WIDTH}px`,
    width: DESKTOP_PANEL_WIDTH,
    minWidth: DESKTOP_PANEL_WIDTH,
    height: '100vh',
    pt: `${NEOBET_HEADER_HEIGHT}px`,
    borderLeft: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
    zIndex: (theme: Theme) => theme.zIndex.modal + 1,
  },
  desktopPanel: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    height: `calc(100vh - ${NEOBET_HEADER_HEIGHT}px)`,
  },
  drawer: (isMobile: boolean) => ({
    zIndex: 1400,
    '& .MuiDrawer-paper': {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      maxWidth: '100%',
      height: 'auto',
      maxHeight: '100dvh',
      bottom: isMobile ? 0 : 16,
      top: 'auto',
      borderRadius: 0,
      backgroundColor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      overflow: 'hidden',
      boxShadow: (theme: Theme) => `0 24px 48px ${alpha(theme.palette.common.black, 0.36)}`,
    },
  }),
  headerTopRow: {
    px: 1.5,
    py: 1,
    minHeight: 48,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'primary.main',
    color: 'background.default',
  },
  headerTitleRow: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
  },
  titleBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    minWidth: 0,
  },
  countBubble: {
    minWidth: 24,
    height: 24,
    borderRadius: 999,
    px: 0.75,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'error.main',
    color: 'neutral.25',
    typography: 'body2',
    fontWeight: 800,
  },
  headerTitle: {
    color: 'background.default',
    typography: 'h4',
    lineHeight: 1,
  },
  headerTitleIcon: {
    color: 'background.default',
    fontSize: 22,
    flexShrink: 0,
  },
  navigationRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  },
  navigationBtn: {
    p: 0,
    color: 'background.default',
  },
  contentScrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  betList: {
    px: 1.25,
    py: 1.25,
    display: 'grid',
    gap: 1,
  },
  footer: {
    px: 1.25,
    py: 1.25,
    borderTop: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'neutral.700',
    display: 'grid',
    gap: 1,
  },
  panelRoot: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    height: '100%',
  },
} as const;

const NeobetReofferedBetslipDrawer = () => {
  const showBetslip = useShouldShowBetslip();
  const {
    open,
    currentBetslip,
    betslip,
    reofferedFromBetslip,
    bets,
    previousBets,
    betBuilderGroups,
    isBanker,
    showBonusData,
    betslipHeaderTitle,
    currentIndex,
    totalSlips,
    isPrevDisabled,
    isNextDisabled,
    goToPrev,
    goToNext,
  } = useReofferedBetslip();

  const { isMobile } = useBreakpoints();

  const isDesktopPanelVisible = !isMobile && open;
  const isMobileDrawerVisible = isMobile && open;

  if (!showBetslip) {
    return null;
  }

  const panelContent = (
    <Box data-testid='neobet-reoffered-betslip-panel' sx={styles.panelRoot}>
      <Box sx={styles.headerTopRow}>
        <Stack direction='row' sx={styles.headerTitleRow}>
          <Box sx={styles.titleBadge}>
            <ReceiptLongOutlinedIcon sx={styles.headerTitleIcon} />
            <Typography
              variant='h5'
              sx={mergeSx(
                {
                  fontWeight: 800,
                },
                styles.headerTitle
              )}
            >
              {betslipHeaderTitle}
            </Typography>
            <Box sx={styles.countBubble}>{bets.length + betBuilderGroups.length}</Box>
          </Box>

          <Stack
            direction='row'
            sx={{
              alignItems: 'center',
              gap: 1,
            }}
          >
            {betslip?.createdAt && (
              <ReofferedBetslipCountdown createdAt={betslip.createdAt} ticketId={currentBetslip} />
            )}

            {totalSlips > 1 && (
              <Box sx={styles.navigationRow}>
                <IconButton disabled={isPrevDisabled} sx={styles.navigationBtn} onClick={goToPrev} size='small'>
                  <NavigateBefore />
                </IconButton>
                <Typography
                  variant='body2'
                  sx={{
                    fontWeight: 700,
                    color: 'background.default',
                  }}
                >
                  {currentIndex + 1}/{totalSlips}
                </Typography>
                <IconButton disabled={isNextDisabled} sx={styles.navigationBtn} onClick={goToNext} size='small'>
                  <NavigateNext />
                </IconButton>
              </Box>
            )}
          </Stack>
        </Stack>
      </Box>

      <Box sx={styles.contentScrollArea} data-testid='neobet-reoffered-betslip-scroll-region'>
        <Box sx={styles.betList}>
          {bets.map((bet, index) => (
            <NeobetReofferedSelectionCard
              key={bet.outcomeId}
              bet={bet}
              previousBet={previousBets[index]}
              bets={bets}
              showBankers={isBanker}
            />
          ))}
          {betBuilderGroups.map((group) => (
            <NeobetReofferedBetBuilderItem key={group.id} group={group} />
          ))}
        </Box>
      </Box>

      <Box sx={styles.footer}>
        {betslip && (
          <NeobetReofferedSummary
            betslip={betslip}
            reofferedFromBetslip={reofferedFromBetslip}
            showBonusData={showBonusData}
          />
        )}

        <ReofferedBetslipActions currentBetslip={currentBetslip} />
      </Box>
    </Box>
  );

  return (
    <>
      {isDesktopPanelVisible ? (
        <Box sx={styles.desktopRail} data-testid='neobet-reoffered-betslip-desktop-rail'>
          <Box sx={styles.desktopPanel}>{panelContent}</Box>
        </Box>
      ) : null}

      {isMobile ? (
        <Drawer
          anchor='bottom'
          open={isMobileDrawerVisible}
          sx={styles.drawer(isMobile)}
          ModalProps={{ keepMounted: true }}
        >
          {panelContent}
        </Drawer>
      ) : null}
    </>
  );
};

export default NeobetReofferedBetslipDrawer;
